import type {
  ResponseProjectionPreviewRequest,
  ToolResponseProjection,
} from '@reach/shared-types/tool-response-projection';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { partnerApi } from '../../lib/api';
import { CopyButton } from './CopyButton';
import { MappedResultPane } from './MappedResultPane';
import { PreviewIssueRow } from './PreviewIssueRow';
import { RESPONSE_MAPPING_COPY } from './response-mapping-copy';
import styles from './ResponseMapping.module.css';

export const RESPONSE_MAPPING_PREVIEW_DEBOUNCE_MS = 400;

interface ResponseMappingPreviewProps {
  sampleResponse: unknown;
  mapping: ToolResponseProjection | null;
}

export const ResponseMappingPreview = ({ sampleResponse, mapping }: ResponseMappingPreviewProps) => {
  const request = {
    sampleResponse,
    responseMapping: mapping,
  } satisfies ResponseProjectionPreviewRequest;
  const mappingIsEmpty = !mapping || Object.keys(mapping).length === 0;
  const sampleIsReady = sampleResponse !== undefined;

  const requestCanonical = JSON.stringify(request);
  const [debouncedRequestCanonical, setDebouncedRequestCanonical] = useState(requestCanonical);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedRequestCanonical(requestCanonical),
      RESPONSE_MAPPING_PREVIEW_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [requestCanonical]);

  const requestIsSettled = debouncedRequestCanonical === requestCanonical;

  const previewQuery = useQuery({
    queryKey: ['response-mapping-preview', debouncedRequestCanonical],
    queryFn: () => partnerApi.previewResponseMapping(request),
    enabled: sampleIsReady && !mappingIsEmpty && requestIsSettled,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  if (!sampleIsReady) {
    return <p className={styles.previewStatus}>{RESPONSE_MAPPING_COPY.previewSamplePrompt}</p>;
  }

  const sourceText = JSON.stringify(sampleResponse, null, 2);
  const mappedValue = mappingIsEmpty ? sampleResponse : previewQuery.data?.mapped;
  const issues = mappingIsEmpty ? [] : (previewQuery.data?.issues ?? []);
  const mappedText = mappedValue === undefined ? null : JSON.stringify(mappedValue, null, 2);

  const previewReturnedNoMappedResult =
    !mappingIsEmpty &&
    previewQuery.isSuccess &&
    !previewQuery.isPlaceholderData &&
    !previewQuery.isFetching &&
    previewQuery.data?.mapped === undefined;
  const previewFailed = previewQuery.isError || previewReturnedNoMappedResult;
  const previewIsStale = !mappingIsEmpty && (previewQuery.isFetching || !requestIsSettled);

  return (
    <section className={styles.preview}>
      <div className={styles.previewPanes}>
        <div>
          <h4 className={styles.paneTitle}>
            {RESPONSE_MAPPING_COPY.previewSourceTitle}
            <CopyButton value={sourceText} />
          </h4>
          <pre className={styles.code}>{sourceText}</pre>
        </div>
        <div>
          <h4 className={styles.paneTitle}>
            {RESPONSE_MAPPING_COPY.previewResultTitle}
            {mappingIsEmpty && (
              <span className={styles.passthroughBadge}>
                {RESPONSE_MAPPING_COPY.previewPassthroughBadge}
              </span>
            )}
          </h4>
          <MappedResultPane previewFailed={previewFailed} mappedText={mappedText} />
        </div>
      </div>

      {mappingIsEmpty && (
        <p className={styles.previewStatus}>{RESPONSE_MAPPING_COPY.passthroughNotice}</p>
      )}
      {previewIsStale && (
        <p className={styles.previewStatus}>{RESPONSE_MAPPING_COPY.previewUpdatingStatus}</p>
      )}
      {issues.length > 0 && (
        <ul className={styles.issueList}>
          {issues.map((issue, index) => (
            <PreviewIssueRow
              key={`${issue.code}-${JSON.stringify(issue.outputPath ?? [])}-${issue.responsePath ?? 'mapping'}-${index}`}
              issue={issue}
            />
          ))}
        </ul>
      )}
    </section>
  );
};
